import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'fr' | 'en';

interface Translations {
  [key: string]: string;
}

const translations: Record<Language, Translations> = {
  fr: {
    // Platform
    'platform.tagline': 'Jeux de société intelligents & interactifs',
    'platform.badge': 'Animé par Intelligence Artificielle',
    'platform.hero.line1': "L'IA devient votre",
    'platform.hero.word1': 'Narrateur',
    'platform.hero.word2': 'Animateur',
    'platform.hero.word3': 'Partenaire de Jeu',
    'platform.description': 'Des expériences immersives avec le meilleur des animateurs.',
    'platform.features': 'Nos Expériences',
    'platform.footer': 'Gratuit • Sans inscription • Sur navigateur',
    
    // Features
    'feature.voice': 'Narration Vocale IA',
    'feature.voice.desc': 'Une IA unique raconte et anime vos parties',
    'feature.multiplayer': 'Multijoueur Local',
    'feature.multiplayer.desc': 'Jouez ensemble, un seul appareil suffit',
    'feature.instant': 'Instantané',
    'feature.instant.desc': 'Aucune inscription, gratuit, jouez maintenant',
    
    // Navigation
    'nav.leaderboards': 'Classements',
    'nav.profile': 'Mon profil',
    'nav.login': 'Connexion',
    'nav.logout': 'Déconnexion',
    'nav.back': 'Retour plateforme SuperSociety.ai',
    
    // Auth
    'auth.login': 'Connexion',
    'auth.signup': 'Inscription',
    'auth.email': 'Email',
    'auth.password': 'Mot de passe',
    'auth.username': 'Pseudo',
    'auth.google': 'Continuer avec Google',
    'auth.or': 'ou',
    'auth.no_account': "Pas encore de compte ?",
    'auth.has_account': "Déjà un compte ?",
    'auth.submit_login': 'Se connecter',
    'auth.submit_signup': "S'inscrire",
    'auth.find_stats': 'Retrouvez vos statistiques',
    'auth.create_account': 'Créez votre compte joueur',
    
    // Profile
    'profile.title': 'Mon Profil',
    'profile.games_played': 'Parties jouées',
    'profile.wins': 'Victoires',
    'profile.favorites': 'Favoris',
    'profile.stats_by_game': 'Statistiques par jeu',
    'profile.no_games': 'Aucune partie jouée pour le moment',
    'profile.favorite_games': 'Jeux favoris',
    'profile.no_favorites': 'Aucun favori ajouté',
    'profile.recent_history': 'Historique récent',
    'profile.no_history': "Aucune partie dans l'historique",
    'profile.points': 'points',
    'profile.games': 'parties',
    
    // Leaderboards
    'leaderboards.title': 'Classements',
    'leaderboards.subtitle': 'Les meilleurs joueurs de SuperSociety.ai',
    'leaderboards.no_players': 'Aucun joueur classé pour le moment.',
    'leaderboards.be_first': 'Soyez le premier !',
    
    // Games - General
    'game.players': 'joueurs',
    'game.play': 'Jouer',
    'game.in_dev': 'En développement',
    'game.coming_soon': 'Bientôt',
    'game.popular': 'Populaire',
    
    // Game: Loup-Garou
    'game.loup-garou.name': 'Loup-Garou',
    'game.loup-garou.description': 'Le célèbre jeu de rôle animé par une IA narrative vocale',
    'game.loup-garou.players': '4-12 joueurs',
    
    // Game: Impro
    'game.impro.name': 'Impro Théâtrale',
    'game.impro.description': 'Improvisez des scènes avec contextes, contraintes et rebondissements IA',
    'game.impro.players': '2-6 joueurs',
    
    // Game: Tribunal
    'game.tribunal.name': 'Le Tribunal',
    'game.tribunal.description': 'Débats moraux, interrogatoires et verdicts avec un procureur IA',
    'game.tribunal.players': '3-8 joueurs',
    
    // Game: Appel Absurde
    'game.appel-absurde.name': 'Appel Absurde',
    'game.appel-absurde.description': "Appelle des gens et place les phrases absurdes de l'IA. Sinon... gage !",
    'game.appel-absurde.players': '2-8 joueurs',
    
    // Game: Murder Mystery
    'game.murder-mystery.name': 'Murder Mystery',
    'game.murder-mystery.description': 'Enquêtez sur un meurtre au manoir, interrogez les suspects et trouvez le coupable',
    'game.murder-mystery.players': '3-6 joueurs',
    
    // Game: RPG IA
    'game.rpg-ia.name': 'Aventure RPG',
    'game.rpg-ia.description': "Une épopée narrative générée et animée par IA où vos choix façonnent l'histoire",
    'game.rpg-ia.players': '1-4 joueurs',
    
    // Game: Startup Pitch
    'game.startup-pitch.name': 'Startup Pitch',
    'game.startup-pitch.description': 'Pitchez votre startup devant des investisseurs IA impitoyables !',
    'game.startup-pitch.players': '2-6 joueurs',
    
    // Startup Pitch game strings
    'startup.voice_pitch': 'Pitch vocal',
    'startup.ai_investors': 'Investisseurs IA',
    'startup.add_players': 'Ajoutez les joueurs',
    'startup.player_name': 'Nom du joueur...',
    'startup.start_game': 'Lancer la partie',
    'startup.turn_of': 'Au tour de',
    'startup.choose_mode': 'Choisissez votre mode de pitch',
    'startup.generated_idea': 'Idée générée par IA',
    'startup.generated_idea_desc': "L'IA vous donne un concept de startup à pitcher",
    'startup.own_idea': 'Ma propre idée',
    'startup.own_idea_desc': 'Pitchez votre propre projet ou invention',
    'startup.generating_brief': 'Génération de votre startup...',
    'startup.pitch_your_idea': 'pitchez votre idée !',
    'startup.your_startup': 'voici votre startup !',
    'startup.problem': 'Problème',
    'startup.concept': 'Concept',
    'startup.market': 'Marché cible',
    'startup.start_pitch': 'Commencer le pitch',
    'startup.pitch_now': 'à vous de pitcher',
    'startup.pitch_instructions': 'Présentez votre startup en 60-90 secondes. Soyez convaincant !',
    'startup.max_90s': 'Maximum 90 secondes',
    'startup.transcribing': 'Transcription en cours...',
    'startup.transcription_error': 'Erreur de transcription',
    'startup.face_investors': 'Affronter les investisseurs',
    'startup.redo_pitch': 'Refaire le pitch',
    'startup.investor_questions': 'Questions des investisseurs',
    'startup.investor_thinking': "L'investisseur réfléchit...",
    'startup.answer': 'Répondre',
    'startup.your_answer': 'Votre réponse',
    'startup.analyzing': 'Analyse en cours...',
    'startup.next_question': 'Question suivante',
    'startup.to_deliberation': 'Délibération',
    'startup.deliberation': 'Délibération en cours...',
    'startup.investors_deliberating': 'Les investisseurs se consultent...',
    'startup.verdict_invest': "J'investis !",
    'startup.verdict_counter': 'Contre-offre',
    'startup.verdict_pass': 'Je passe',
    'startup.for': 'pour',
    'startup.scores': 'Détail des scores',
    'startup.score_clarity': 'Clarté',
    'startup.score_viability': 'Viabilité',
    'startup.score_charisma': 'Charisme',
    'startup.score_responseQuality': 'Réponses',
    'startup.score_innovation': 'Innovation',
    'startup.total': 'Total',
    'startup.strengths': 'Points forts',
    'startup.weaknesses': 'Points faibles',
    'startup.next_pitcher': 'Prochain pitcher',
    'startup.see_results': 'Voir les résultats',
    'startup.final_ranking': 'Classement final',
    'startup.play_again': 'Rejouer',
    'startup.mic_error': 'Erreur micro',
    'startup.mic_error_desc': "Impossible d'accéder au microphone",
    'startup.own_idea_instructions': 'Vous allez pitcher votre propre idée. Préparez votre argumentaire !',
    'startup.fallback_feedback': 'Pitch intéressant avec du potentiel mais qui manque de détails.',
    'startup.fallback_strength': 'Bonne énergie',
    'startup.fallback_weakness': 'Manque de chiffres',
    'startup.fallback_quote': 'Intéressant, mais j\'ai besoin de plus de concret.',
    
    // Share
    'share.title': 'Partagez votre résultat',
    'share.copied': 'Lien copié !',
    'share.copied_desc': 'Le lien a été copié dans le presse-papier.',
    
    // Results
    'result.win': 'Victoire',
    'result.loss': 'Défaite',
    'result.draw': 'Égalité',
    
    // Common
    'common.loading': 'Chargement...',
    'common.error': 'Erreur',
    'common.retry': 'Réessayer',
    'common.save': 'Enregistrer',
    'common.cancel': 'Annuler',
  },
  en: {
    // Platform
    'platform.tagline': 'Smart & interactive board games',
    'platform.badge': 'Powered by Artificial Intelligence',
    'platform.hero.line1': 'AI becomes your',
    'platform.hero.word1': 'Narrator',
    'platform.hero.word2': 'Host',
    'platform.hero.word3': 'Game Partner',
    'platform.description': 'Immersive experiences with the best game host.',
    'platform.features': 'Our Experiences',
    'platform.footer': 'Free • No signup • On browser',
    
    // Features
    'feature.voice': 'AI Voice Narration',
    'feature.voice.desc': 'A unique AI narrates and hosts your games',
    'feature.multiplayer': 'Local Multiplayer',
    'feature.multiplayer.desc': 'Play together, one device is enough',
    'feature.instant': 'Instant',
    'feature.instant.desc': 'No signup, free, play now',
    
    // Navigation
    'nav.leaderboards': 'Leaderboards',
    'nav.profile': 'My profile',
    'nav.login': 'Login',
    'nav.logout': 'Logout',
    'nav.back': 'Back to SuperSociety.ai',
    
    // Auth
    'auth.login': 'Login',
    'auth.signup': 'Sign up',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.username': 'Username',
    'auth.google': 'Continue with Google',
    'auth.or': 'or',
    'auth.no_account': "Don't have an account?",
    'auth.has_account': "Already have an account?",
    'auth.submit_login': 'Log in',
    'auth.submit_signup': 'Sign up',
    'auth.find_stats': 'Find your statistics',
    'auth.create_account': 'Create your player account',
    
    // Profile
    'profile.title': 'My Profile',
    'profile.games_played': 'Games played',
    'profile.wins': 'Wins',
    'profile.favorites': 'Favorites',
    'profile.stats_by_game': 'Stats by game',
    'profile.no_games': 'No games played yet',
    'profile.favorite_games': 'Favorite games',
    'profile.no_favorites': 'No favorites added',
    'profile.recent_history': 'Recent history',
    'profile.no_history': 'No games in history',
    'profile.points': 'points',
    'profile.games': 'games',
    
    // Leaderboards
    'leaderboards.title': 'Leaderboards',
    'leaderboards.subtitle': 'Top players on SuperSociety.ai',
    'leaderboards.no_players': 'No players ranked yet.',
    'leaderboards.be_first': 'Be the first!',
    
    // Games - General
    'game.players': 'players',
    'game.play': 'Play',
    'game.in_dev': 'In development',
    'game.coming_soon': 'Coming soon',
    'game.popular': 'Popular',
    
    // Game: Loup-Garou
    'game.loup-garou.name': 'Werewolf',
    'game.loup-garou.description': 'The famous role-playing game hosted by an AI voice narrator',
    'game.loup-garou.players': '4-12 players',
    
    // Game: Impro
    'game.impro.name': 'Improv Theater',
    'game.impro.description': 'Improvise scenes with AI-generated contexts, constraints and plot twists',
    'game.impro.players': '2-6 players',
    
    // Game: Tribunal
    'game.tribunal.name': 'The Tribunal',
    'game.tribunal.description': 'Moral debates, interrogations and verdicts with an AI prosecutor',
    'game.tribunal.players': '3-8 players',
    
    // Game: Appel Absurde
    'game.appel-absurde.name': 'Absurd Call',
    'game.appel-absurde.description': 'Call people and slip in absurd AI phrases. Fail and face a dare!',
    'game.appel-absurde.players': '2-8 players',
    
    // Game: Murder Mystery
    'game.murder-mystery.name': 'Murder Mystery',
    'game.murder-mystery.description': 'Investigate a manor murder, interrogate suspects and find the culprit',
    'game.murder-mystery.players': '3-6 players',
    
    // Game: RPG IA
    'game.rpg-ia.name': 'RPG Adventure',
    'game.rpg-ia.description': 'An AI-generated narrative epic where your choices shape the story',
    'game.rpg-ia.players': '1-4 players',
    
    // Game: Startup Pitch
    'game.startup-pitch.name': 'Startup Pitch',
    'game.startup-pitch.description': 'Pitch your startup to ruthless AI investors!',
    'game.startup-pitch.players': '2-6 players',
    
    // Startup Pitch game strings
    'startup.voice_pitch': 'Voice pitch',
    'startup.ai_investors': 'AI Investors',
    'startup.add_players': 'Add players',
    'startup.player_name': 'Player name...',
    'startup.start_game': 'Start game',
    'startup.turn_of': "It's",
    'startup.choose_mode': 'Choose your pitch mode',
    'startup.generated_idea': 'AI-generated idea',
    'startup.generated_idea_desc': 'The AI gives you a startup concept to pitch',
    'startup.own_idea': 'My own idea',
    'startup.own_idea_desc': 'Pitch your own project or invention',
    'startup.generating_brief': 'Generating your startup...',
    'startup.pitch_your_idea': 'pitch your idea!',
    'startup.your_startup': "here's your startup!",
    'startup.problem': 'Problem',
    'startup.concept': 'Concept',
    'startup.market': 'Target market',
    'startup.start_pitch': 'Start pitching',
    'startup.pitch_now': "it's your turn to pitch",
    'startup.pitch_instructions': 'Present your startup in 60-90 seconds. Be convincing!',
    'startup.max_90s': 'Maximum 90 seconds',
    'startup.transcribing': 'Transcribing...',
    'startup.transcription_error': 'Transcription error',
    'startup.face_investors': 'Face the investors',
    'startup.redo_pitch': 'Redo pitch',
    'startup.investor_questions': 'Investor questions',
    'startup.investor_thinking': 'The investor is thinking...',
    'startup.answer': 'Answer',
    'startup.your_answer': 'Your answer',
    'startup.analyzing': 'Analyzing...',
    'startup.next_question': 'Next question',
    'startup.to_deliberation': 'Deliberation',
    'startup.deliberation': 'Deliberating...',
    'startup.investors_deliberating': 'The investors are conferring...',
    'startup.verdict_invest': "I'm in!",
    'startup.verdict_counter': 'Counter-offer',
    'startup.verdict_pass': "I'm out",
    'startup.for': 'for',
    'startup.scores': 'Score breakdown',
    'startup.score_clarity': 'Clarity',
    'startup.score_viability': 'Viability',
    'startup.score_charisma': 'Charisma',
    'startup.score_responseQuality': 'Responses',
    'startup.score_innovation': 'Innovation',
    'startup.total': 'Total',
    'startup.strengths': 'Strengths',
    'startup.weaknesses': 'Weaknesses',
    'startup.next_pitcher': 'Next pitcher',
    'startup.see_results': 'See results',
    'startup.final_ranking': 'Final ranking',
    'startup.play_again': 'Play again',
    'startup.mic_error': 'Mic error',
    'startup.mic_error_desc': 'Cannot access microphone',
    'startup.own_idea_instructions': "You'll pitch your own idea. Prepare your argument!",
    'startup.fallback_feedback': 'Interesting pitch with potential but lacks details.',
    'startup.fallback_strength': 'Good energy',
    'startup.fallback_weakness': 'Needs more numbers',
    'startup.fallback_quote': "Interesting, but I need more concrete details.",
    
    // Share
    'share.title': 'Share your result',
    'share.copied': 'Link copied!',
    'share.copied_desc': 'The link has been copied to clipboard.',
    
    // Results
    'result.win': 'Win',
    'result.loss': 'Loss',
    'result.draw': 'Draw',
    
    // Common
    'common.loading': 'Loading...',
    'common.error': 'Error',
    'common.retry': 'Retry',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
  },
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('language');
      if (saved === 'en' || saved === 'fr') return saved;
      // Detect browser language
      const browserLang = navigator.language.slice(0, 2);
      return browserLang === 'en' ? 'en' : 'fr';
    }
    return 'fr';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || translations['fr'][key] || key;
  };

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageToggle = () => {
  const { language, setLanguage } = useLanguage();

  return (
    <button
      onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
      className="px-3 py-1.5 rounded-full bg-card/30 border border-border/30 text-muted-foreground hover:text-foreground hover:border-border transition-colors text-sm font-medium"
      aria-label="Toggle language"
    >
      {language === 'fr' ? '🇬🇧 EN' : '🇫🇷 FR'}
    </button>
  );
};
